import React, { useState } from 'react';
import { KompisProductShelf } from '@inter-ikea-kompis/react-components';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import {
  getShoppingListService,
  getShoppingCartService,
} from '../../services/ServiceHandler';
import { renderToast } from '../../services/toastMaster';
import { flatten, unique } from '../../util/array';
import { selectTac } from '../../state/tac/tacSelectors';
import { selectKompisTranslations } from '../../state/translations/translationsSelectors';
import { selectDexfSettings } from '../../state/dexfSettings/dexfSettingsSelectors';

import {
  selectYmalArticleGroups,
  selectYmalArticles,
  selectYMALsData,
} from '../../state/ymal/ymalSelectors';
import { getShoppingItemsFromTAC } from '../../util/aactools/kompisConvert';
import styles from './SummaryPage.module.less';
import { translate } from '../../services/L10n';
import localStatisticsReporter from '../../services/statistics/insights/custom/local/localStatisticsReporter';
import mandatoryStatisticsReporter from '../../services/statistics/insights/mandatory/mandatoryStatisticsReporter';
import { ArticleGroup } from '../../state/ymal/ymalTypes';
import { IShoppingItem } from '@inter-ikea-kompis/types';
import { useSelector } from 'react-redux';
import { t } from '../../translations';

export const Ymal: React.FC = () => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [itemsAddedToCart, setItemsAddedToCart] = useState<Array<string>>([]);
  const [itemsAddedToList, setItemsAddedToList] = useState<Array<string>>([]);
  const [loadingItemsAddedToCart, setLoadingItemsAddedToCart] = useState<
    Array<string>
  >([]);
  const [loadingItemsAddedToList, setLoadingItemsAddedToList] = useState<
    Array<string>
  >([]);

  const tac = useSelector(selectTac);
  if (!tac) return null;
  const articleGroups = useSelector(selectYmalArticleGroups);
  const articles = useSelector(selectYmalArticles);
  const ymals = useSelector(selectYMALsData);
  const kompisTranslations = useSelector(selectKompisTranslations);
  const dexfSettings = useSelector(selectDexfSettings);
  const shoppingList = getShoppingItemsFromTAC(tac);

  const shouldDisplayArticleGroup = (
    articleGroup: ArticleGroup,
    shoppingList: IShoppingItem[]
  ) => {
    return (
      articleGroup.showForAll ||
      articleGroup.products?.some(product => {
        return shoppingList.some(item => item.id === product.id);
      })
    );
  };

  const getEnabledArticleIds = (articleGroup: ArticleGroup) => {
    const { id } = articleGroup;
    return articles
      .filter((article: any) => {
        return article.articleGroups[id];
      })
      .map(article => article.id);
  };

  const getYmalIdsForCurrentShoppingList = (shoppingList: IShoppingItem[]) => {
    return flatten(
      articleGroups
        .filter(articleGroup =>
          shouldDisplayArticleGroup(articleGroup, shoppingList)
        )
        .map(articleGroup => getEnabledArticleIds(articleGroup))
    );
  };

  const getYmalArticleBasedOnId = (articleId: string) => {
    return ymals.find(ymalArticle =>
      [
        ymalArticle.content.ruItemNo,
        ymalArticle.content.itemNoGlobal,
        ymalArticle.content.itemNoLocal,
      ]
        .filter(Boolean)
        .some(id => id === articleId)
    );
  };

  const shoppingListArticlesToShowIds =
    getYmalIdsForCurrentShoppingList(shoppingList);
  const kompisFormattedProducts = shoppingListArticlesToShowIds
    .filter(unique)
    .map(article => getYmalArticleBasedOnId(article))
    .filter(Boolean);

  /**
   * Reports an event based on whether;
   * 1) An item was added to the bag/cart
   * 2) An item was added to the list
   * and if the event was succesful.
   * @param shoppingItems
   * @param {string} destination
   * @param {boolean} success
   */
  const reportYmalEvent = (
    shoppingItems: IShoppingItem[],
    destination: string,
    success: boolean
  ) => {
    if (destination === 'bag') {
      success
        ? mandatoryStatisticsReporter.reportAddToBagSuccess(shoppingItems)
        : localStatisticsReporter.reportAddToBagError(shoppingItems);
    } else {
      success
        ? mandatoryStatisticsReporter.reportAddToListSuccess(shoppingItems)
        : localStatisticsReporter.reportAddToListError(shoppingItems);
    }
  };

  /**
   * Sets the loading state of the add-to-cart button.
   * Bouncing ball animation is shown.
   * @param {string} itemId
   */
  const setAddToCartLoadingState = (itemId: string) => {
    setLoadingItemsAddedToCart([...loadingItemsAddedToCart, itemId]);
  };

  /**
   * Removes the loading state of the add-to-cart button.
   * @param {string} itemId
   */
  const removeAddToCartLoadingState = (itemId: string) => {
    setLoadingItemsAddedToCart(
      loadingItemsAddedToCart.filter((id: string) => id !== itemId)
    );
  };

  /**
   * Sets the loading state of the add-to-list button.
   * Bouncing ball animation is shown.
   * @param {string} itemId
   */
  const setAddToListLoadingState = (itemId: string) => {
    setLoadingItemsAddedToList([...loadingItemsAddedToList, itemId]);
  };

  /**
   * Removes the loading state of the add-to-list button.
   * @param {string} itemId
   */
  const removeAddToListLoadingState = (itemId: string) => {
    setLoadingItemsAddedToList(
      loadingItemsAddedToList.filter((id: string) => id !== itemId)
    );
  };

  /**
   * Sets the loading state of the cart.
   * Attempts to add relevant product to the cart.
   * Reports adding of the product to analytics.
   * Shows the relevant toast if its successfully added or not.
   * @param event
   */
  const onAddToCart = async (event: any) => {
    const itemId = event.detail.shoppingItems[0]?.id;
    setAddToCartLoadingState(itemId);
    const shoppingCartService = getShoppingCartService();
    let shoppingCart;
    try {
      shoppingCart =
        shoppingCartService &&
        (await shoppingCartService.addToCart({
          items: event.detail.shoppingItems,
        }));
    } catch (error) {
      renderToast('cart', false, true);
      removeAddToCartLoadingState(itemId);
      return;
    }
    const isSuccessful = shoppingCart && shoppingCart.errorList.length === 0;
    if (isSuccessful) {
      reportYmalEvent(event.detail.shoppingItems, 'bag', isSuccessful);
      setItemsAddedToCart([...itemsAddedToCart, itemId]);
      renderToast('cart', isSuccessful, true);
      removeAddToCartLoadingState(itemId);
      return;
    } else {
      renderToast('cart', !!isSuccessful, true);
      removeAddToCartLoadingState(itemId);
    }
  };

  /**
   * Sets the loading state of the list.
   * Attempts to add relevant product to the list.
   * Reports adding of the product to analytics.
   * Shows the relevant toast if its succesfully added or not.
   * @param event
   */
  const onAddToList = async (event: any) => {
    const itemId = event.detail.shoppingItems[0]?.id;
    setAddToListLoadingState(itemId);
    const shoppingListService = getShoppingListService();
    let shoppingList;
    try {
      shoppingList =
        shoppingListService &&
        (await shoppingListService.addToList({
          items: event.detail.shoppingItems,
        }));
    } catch (error) {
      removeAddToListLoadingState(itemId);
      renderToast('list', false, true);
      return;
    }
    const isSuccessful = shoppingList && shoppingList.errorList.length === 0;
    if (isSuccessful) {
      reportYmalEvent(event.detail.shoppingItems, 'list', isSuccessful);
      setItemsAddedToList([...itemsAddedToList, itemId]);
      removeAddToListLoadingState(itemId);
      renderToast('list', isSuccessful, true);
    } else {
      removeAddToListLoadingState(itemId);
      renderToast('list', !!isSuccessful, true);
    }
  };
  return (
    <React.Fragment>
      {!!kompisFormattedProducts.length && (
        <div className={styles.ymal}>
          <h2 className={styles.titleProductShelf}>
            {' '}
            {translate(t.YMAL_HEADING)}{' '}
          </h2>
          <KompisProductShelf
            theme={SkapaTheme}
            products={kompisFormattedProducts}
            selectedIndex={selectedIndex}
            translations={kompisTranslations}
            settings={dexfSettings}
            itemsAddedToCart={itemsAddedToCart}
            itemsAddedToList={itemsAddedToList}
            loadingItemsAddedToCart={loadingItemsAddedToCart}
            loadingItemsAddedToList={loadingItemsAddedToList}
            onAddToList={event => onAddToList(event)}
            onAddToCart={event => onAddToCart(event)}
            onChange={event => setSelectedIndex(event.detail.selectedIndex)}
          />
        </div>
      )}
    </React.Fragment>
  );
};
